/**
 * AdminSensorStatusPanel React component
 * 
 * @author    Jens Gruschel
 * @copyright © 2019 contexagon GmbH
 */


// React
import React from 'react';
import connect from "react-redux/es/connect/connect";

// Material UI
import { withStyles } from '@material-ui/core/styles';

// Material UI components
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import Tooltip from "@material-ui/core/Tooltip";
import Link from '@material-ui/core/Link';

// Material UI icons
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';

// application components
import WarningPanel from "../base/WarningPanel";

// application
import {fetchJSON} from "../../operations/communication";
import {
    REST_CALL_UPDATE_SERVER_SENSOR_STATE,
    REST_SERVER_BASE_URL
} from "../../config";
import {editObject} from "../../operations/editing";


/**
 * Panel for admin page to show status status fetched from server
 *
 * @author Jens Gruschel
 */
class AdminSensorStatusPanel extends React.Component {

    state = {
        disconnected: false,
        beaconStatus: [],
        badgeSensorStatus: [],
        codeReaderStatus: []
    };

    componentDidMount() {
        this.refreshData();
        this.timer = setInterval(() => {this.refreshData()}, 5000);
    }

    componentWillUnmount() {
        clearInterval(this.timer);
    }

    refreshData() {
        this.refreshSensorStatus();
    }

    async refreshSensorStatus() {
        
        const response = await this.fetchSensorStatus();

        if (response) {
            const beaconStatus = response.beaconstatus || [];
            const badgeSensorStatus = response.badgesensorstatus || [];
            const codeReaderStatus = response.codereaderstatus || [];
            this.setState({
                disconnected: false,
                beaconStatus: beaconStatus.sort((a, b) => a.section.localeCompare(b.section) || a.name.localeCompare(b.name) || a.identifier.localeCompare(b.identifier) || a.range - b.range),
                badgeSensorStatus: badgeSensorStatus.sort((a, b) => a.section.localeCompare(b.section) || a.name.localeCompare(b.name) || a.range - b.range),
                codeReaderStatus: codeReaderStatus.sort((a, b) => a.section.localeCompare(b.section) || a.name.localeCompare(b.name))
            });
        }
        else {
            this.setState({
                disconnected: true
            });
        }
    }

    render() {

        const { classes } = this.props;

        return <div className={classes.panel}>

            {
                this.state.disconnected && <WarningPanel text="Verbindung zum Server unterbrochen!" />
            }

            <ExpansionPanel defaultExpanded={true}>

                <ExpansionPanelSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography>Beacons</Typography>
                </ExpansionPanelSummary>

                <ExpansionPanelDetails>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Status</TableCell>
                                <TableCell>Sektion</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>ID</TableCell>
                                <TableCell align="right">Bereich</TableCell>
                                <TableCell align="right">Besucher</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {this.state.beaconStatus.map(item => this.renderItem(item, true, true))}
                        </TableBody>
                    </Table>
                </ExpansionPanelDetails>

            </ExpansionPanel>

            <ExpansionPanel defaultExpanded={true}>

                <ExpansionPanelSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography>Badge-Sensoren</Typography>
                </ExpansionPanelSummary>

                <ExpansionPanelDetails>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Status</TableCell>
                                <TableCell>Sektion</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell align="right">Bereich</TableCell>
                                <TableCell align="right">Besucher</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {this.state.badgeSensorStatus.map(item => this.renderItem(item, false, true))}
                        </TableBody>
                    </Table>
                </ExpansionPanelDetails>

            </ExpansionPanel>

            <ExpansionPanel defaultExpanded={true}>

                <ExpansionPanelSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography>Code-Reader</Typography>
                </ExpansionPanelSummary>

                <ExpansionPanelDetails>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Status</TableCell>
                                <TableCell>Sektion</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell align="right">Besucher</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {this.state.codeReaderStatus.map(item => this.renderItem(item, false, false))}
                        </TableBody>
                    </Table>
                </ExpansionPanelDetails>

            </ExpansionPanel>

            <Typography>sichtbar sind nur solche Sensoren, die in letzter Zeit aktiviert wurden</Typography>

        </div>;
    }

    renderItem(item, includeIdentifier, includeRange) {

        const state = item.visitors.length > 0 ? "active" : "inactive";
        const icon = item.visitors.length > 0 ? <RadioButtonCheckedIcon color="primary" /> : <RadioButtonUncheckedIcon color="primary" />;

        return <TableRow key={item.identifier + "/" + item.range}>
            <TableCell>
                <Tooltip title={state}>{icon}</Tooltip>
            </TableCell>
            <TableCell>
                {item.section}
            </TableCell>
            <TableCell>
                <Link component="button" style={{fontSize: "13px"}} onClick={() => {this.props.editObject(item.id);}}>
                    {item.name || "unbenannt"}
                </Link>
            </TableCell>
            {includeIdentifier && <TableCell>
                {item.identifier}
            </TableCell>}
            {includeRange && <TableCell align="right">
                {item.range.toFixed(1)}
            </TableCell>}
            <TableCell align="right">
                <Tooltip title={item.visitors.map(visitor => visitor.id).join(", ")}>
                    <div>{item.visitors.length}</div>
                </Tooltip>
            </TableCell>
        </TableRow>;
    }

    async fetchSensorStatus() {
        try {
            const response = await fetchJSON(
                REST_SERVER_BASE_URL + REST_CALL_UPDATE_SERVER_SENSOR_STATE.path, {
                    method: REST_CALL_UPDATE_SERVER_SENSOR_STATE.method,
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + this.props.accessToken
                    }
                }
            );
            return response;
        }
        catch (error) {
            return null;
        }
    }
}

const styles = theme => ({
    panel: {
        width: '100%'
    },
    noLineBreak: {
        maxWidth: 300,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
    }
});

const mapStateToProps = state => {
    return {
        accessToken: state.authorization.accessToken
    };
};

export default connect(mapStateToProps, {editObject})(withStyles(styles)(AdminSensorStatusPanel));